import React, { useState, useEffect, useMemo } from 'react';
import { Box, Stack, TableCell } from '@mui/material';
import TopBar from '../../../../../component/TopBar';
import DataTable from "../../../../../component/DataTable";
import { expenseColumns } from "./expenseColumns";
import MainBox from '../../../container/MainBox';
import BtnPrimary from '../../../../../component/PrimaryButton';
import { SearchInput, FilterSelect } from "../../../../../component/listing";
import { getEmployeeExpense } from '../../../../../services/expense';
import { useNotify } from '../../../../../component/NotificationProvider';
import Counter from '../../../../../component/Counter';
import { counterRowSx } from '../../../container/frameLayout';
import { claimTotals, claimCountLabel, listedTotal } from './claimFacts';
import { tokens } from '../../../../../theme';


const money = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;

const FOOT_SX = { borderBottom: "none", fontSize: "0.875rem", color: tokens.text, py: "0.875rem" };

const MyExpenses = () => {
  const notify = useNotify();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getEmployeeExpense();
      if (res?.success === false) throw new Error(res?.message || "Failed to load expenses");
      setData((Array.isArray(res?.data) ? res.data : []).filter((e) => e.type === "ANY").reverse());
    } catch (err) {
      setError(true);
      notify.error(err?.message || "Failed to load expenses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => claimTotals(data), [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((e) => {
      const matchesStatus = statusFilter === "all" || e.status === statusFilter;
      const matchesSearch = !q || (e?.policyCateogary?.name || "").toLowerCase().includes(q);
      return matchesStatus && matchesSearch;
    });
  }, [data, search, statusFilter]);

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Expenses"} breadcrumbs={[{ label: "Expenses", href: "/app/employee-expense" }, { label: "My Expenses" }]} />

      {/* The same four figures as the overview, from the same function. The
          full list is where somebody comes to chase a specific claim, and
          arriving without the totals meant leaving to find them. */}
      <Box sx={{ ...counterRowSx(4), mb: "1.25rem" }}>
        <Counter
          label="Pending"
          value={loading ? "—" : money(totals.pending.amount)}
          sub={claimCountLabel(totals.pending.count, "nothing waiting")}
          tone={totals.pending.count ? tokens.warningText : undefined}
        />
        <Counter
          label="Approved, not yet paid"
          value={loading ? "—" : money(totals.awaiting.amount)}
          sub={totals.awaiting.count ? `${claimCountLabel(totals.awaiting.count)}, paid with salary` : "nothing outstanding"}
        />
        <Counter
          label={`Reimbursed ${totals.year}`}
          value={loading ? "—" : money(totals.reimbursed.amount)}
          sub={claimCountLabel(totals.reimbursed.count, "none this year")}
        />
        <Counter
          label={`Rejected ${totals.year}`}
          value={loading ? "—" : money(totals.rejected.amount)}
          sub={claimCountLabel(totals.rejected.count, "none this year")}
          tone={totals.rejected.count ? tokens.danger : undefined}
        />
      </Box>

      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ md: "center" }} sx={{ mb: "1.25rem" }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: "wrap" }}>
            <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search category" minWidth="14rem" />
            <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              options={[{ value: "all", label: "All statuses" }, { value: "pending", label: "Pending" }, { value: "approved", label: "Approved" }, { value: "rejected", label: "Rejected" }]} />
          </Stack>
          <BtnPrimary to="/app/employee-expense/add-new" sx={{ borderRadius: "8px", whiteSpace: "nowrap" }}>
            + Add New Expense
          </BtnPrimary>
        </Stack>

        <DataTable
          rows={filtered}
          loading={loading}
          error={error}
          onRetry={fetchData}
          errorTitle="Couldn't load expenses"
          empty={{
            title: data.length === 0 ? "No expenses yet" : "No matches",
            description: data.length === 0 ? "Submit your first expense claim." : "Try a different search or filter.",
          }}
          tableSx={{ width: "100%", tableLayout: "fixed" }}
          columns={expenseColumns(money)}
          /* Totals what the filter left on screen, not the whole history — a
             footer under "rejected only" that reported every claim ever made
             would be answering a question nobody asked. */
          footer={
            filtered.length ? (
              <>
                <TableCell sx={{ ...FOOT_SX, fontWeight: 600 }}>
                  {claimCountLabel(listedTotal(filtered).count)}
                </TableCell>
                <TableCell sx={FOOT_SX} />
                <TableCell sx={{ ...FOOT_SX, fontWeight: 600, textAlign: "right" }}>
                  {money(listedTotal(filtered).amount)}
                </TableCell>
                <TableCell sx={FOOT_SX} />
              </>
            ) : null
          }
        />
      </MainBox>
    </Box>
  );
};

export default MyExpenses;
